import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { ClipboardList, Eye, Clock, AlertCircle, Loader2, Building2, ExternalLink, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export default function PendientesAprobacion() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingWa, setSendingWa] = useState(null);

  const sendWa = async (e, p, tipo) => {
    e.stopPropagation();
    setSendingWa(p.id);
    try {
      await api.post("/whatsapp/portal/share", { peticion_id: p.id, tipo });
      toast.success(`📱 Link enviado al cliente por WhatsApp`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error enviando WhatsApp");
    } finally {
      setSendingWa(null);
    }
  };

  useEffect(() => {
    api.get("/aprobaciones-pendientes")
      .then(({ data }) => setItems(data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="p-8 max-w-6xl">
      <PageHeader title="Pendientes de aprobación cliente"
        subtitle="Peticiones esperando que el cliente apruebe presupuesto o AAFF · ordenado por interés del cliente" />

      {loading && <div className="text-center py-12 text-zinc-500"><Loader2 className="w-6 h-6 animate-spin inline" /></div>}

      {!loading && items.length === 0 && (
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-12 text-center">
          <ClipboardList className="w-12 h-12 mx-auto text-emerald-400 opacity-60 mb-3" />
          <div className="text-zinc-300 font-semibold">¡Todo al día!</div>
          <div className="text-xs text-zinc-500 mt-1">No hay peticiones esperando aprobación del cliente.</div>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="space-y-2" data-testid="pendientes-list">
          {items.map((p) => {
            const visto = p.portal_views_count > 0;
            const urgente = (p.days_waiting || 0) > 7;
            return (
              <button key={p.id} onClick={() => nav(`/peticiones/${p.id}`)}
                data-testid={`pendiente-${p.codigo}`}
                className="w-full text-left bg-zinc-900/60 border border-zinc-800 hover:border-amber-500/40 rounded-xl p-3 transition-colors">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap min-w-0 flex-1">
                    <span className="text-[10px] text-amber-400 font-mono shrink-0">{p.codigo}</span>
                    <span className="text-sm font-semibold truncate">{p.nombre}</span>
                    {p.marca && <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300">{p.marca}</span>}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {p.pendientes.includes("presupuesto") && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-amber-500/20 text-amber-200">⏳ Presupuesto</span>
                    )}
                    {p.pendientes.includes("aaff_global") && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-violet-500/20 text-violet-200">⏳ AAFF</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-2 text-xs flex-wrap">
                  <span className="text-zinc-400 flex items-center gap-1">
                    <Building2 className="w-3 h-3" /> {p.cliente_nombre}
                  </span>
                  <span className={`flex items-center gap-1 ${visto ? "text-emerald-300" : "text-zinc-500"}`}>
                    <Eye className="w-3 h-3" /> {visto ? `${p.portal_views_count} visita(s)` : "Sin abrir"}
                  </span>
                  {p.days_waiting != null && (
                    <span className={`flex items-center gap-1 ${urgente ? "text-red-300 font-semibold" : "text-zinc-500"}`}>
                      <Clock className="w-3 h-3" /> {p.days_waiting === 0 ? "Hoy" : `${p.days_waiting}d esperando`}
                      {urgente && <AlertCircle className="w-3 h-3" />}
                    </span>
                  )}
                  <span className="ml-auto text-amber-300 text-[11px] flex items-center gap-0.5">
                    Abrir <ExternalLink className="w-2.5 h-2.5" />
                  </span>
                </div>
                {visto && (
                  <div className="mt-1.5 text-[10px] text-emerald-200 italic">
                    💡 El cliente abrió el portal pero aún no ha actuado. Buen momento para llamada de seguimiento.
                  </div>
                )}
                <div className="mt-2 flex gap-1.5">
                  <button
                    onClick={(e) => sendWa(e, p, p.pendientes.includes("aaff") ? "aaff" : "presupuesto")}
                    disabled={sendingWa === p.id}
                    className="text-[10px] px-2 py-1 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25 disabled:opacity-50 flex items-center gap-1"
                    data-testid={`wa-resend-${p.codigo}`}>
                    {sendingWa === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <MessageCircle className="w-3 h-3" />}
                    Reenviar link por WhatsApp
                  </button>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
